import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  OrderedPageFilter,
  PagedProspects,
  SaveProspectRequest,
  defaultFilterValues,
  getProspects,
  pagedColumnDefaultValues,
  saveProspect,
} from 'core'

import { Card, Col, Row, RowWithAction, Notification as notification } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import ProspectForm from './prospectForm'
import ProspectsTable from './prospectsTable'

const { ICHRA_DECISION_TOOL_PATH, UPLOAD_EMPLOYEE_CENSUS_PATH, PROPOSAL_PATH } = ROUTE_PATHS

const Prospects: React.FC = () => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const history = useHistory()

  const [prospectsFilter, setProspectsFilter] = useState<OrderedPageFilter>(defaultFilterValues)
  const [prospectId, setProspectId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const {
    data: prospects,
    sendQuery: sendGetProspects,
    isLoading: loadingProspects,
    reload: reloadProspects,
  } = useQueryRequestV2<PagedProspects>({
    queryFn: () => getProspects(prospectsFilter),
    initialState: pagedColumnDefaultValues,
  })

  const { sendCommand: sendSaveProspect } = useCommandRequestV2(saveProspect)

  const submitForm = async (values: SaveProspectRequest) => {
    setIsSubmitting(true)
    const result = await sendSaveProspect(values)

    result.errors.length === 0
      ? (notification.open({
          message: translate('save-prospect.saved'),
          type: 'success',
        }),
        reloadProspects(),
        setProspectId(null))
      : notification.open({
          message: result.errors[0],
          type: 'error',
        })

    setIsSubmitting(false)
  }

  const onDownloadReport = (id: string) => {}

  return (
    <>
      <Row className="mt-24">
        <Col span={24}>
          <Card
            title={
              <RowWithAction
                title={translate('prospects')}
                iconName="far fa-plus"
                onActionClick={() => setProspectId('')}
                actionButtonTitle={translate('new-prospect')}
              />
            }
          >
            <ProspectsTable
              searchFilter={prospectsFilter}
              setSearchFilter={async (filter) => {
                await sendGetProspects(() => getProspects(filter))
                setProspectsFilter(filter)
              }}
              prospects={prospects}
              loadingProspects={loadingProspects}
              history={history}
              translate={translate}
              onEdit={(id) => setProspectId(id)}
              onStartProposal={(id: string, companyName: string) =>
                history.push(`${ICHRA_DECISION_TOOL_PATH}/${PROPOSAL_PATH}?id=${id}&companyName=${companyName}`)
              }
              onUploadEmployeeCensus={(id: string, companyName: string) =>
                history.push(
                  `${ICHRA_DECISION_TOOL_PATH}/${UPLOAD_EMPLOYEE_CENSUS_PATH}?id=${id}&companyName=${companyName}`,
                )
              }
            />
          </Card>
        </Col>
      </Row>

      {prospectId !== null && (
        <ProspectForm
          prospectId={prospectId}
          visible={prospectId !== null}
          onCancel={() => setProspectId(null)}
          onSubmit={submitForm}
          submitting={isSubmitting}
        />
      )}
    </>
  )
}

export default Prospects
